import { GuestAreaPromoBanner } from "@/components/GuestAreaPromoBanner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import {
  useEliteReservation,
  useEliteStructure,
} from "@/contexts/EliteTemporaryContextProvider";
import { useGuestAuth } from "@/contexts/GuestAuthContext";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { getTranslatedName } from "@/lib/utils";
import type { WebCheckInReservationData } from "@/types/Reservation";
import {
  BedDouble,
  Building2,
  Calendar,
  CheckCircle2,
  ChevronRight,
  ClipboardCheck,
  HandPlatter,
  MapPin,
  MapPinned,
  Navigation,
  Phone,
  RefreshCw,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

export default function Welcome() {
  const {
    reservation,
    isLoading: isReservationLoading,
    refreshReservation,
  } = useEliteReservation();
  const { structure, getStructure } = useEliteStructure();
  const { profile, isLoading: isAuthLoading } = useGuestAuth();
  const { t, i18n } = useTranslation("common");

  useDocumentTitle(t("welcome") !== "welcome" ? t("welcome") : "Benvenuto");

  const storedStructureId =
    typeof window !== "undefined" ? localStorage.getItem("structureId") : null;
  const hasReservationToken =
    typeof window !== "undefined"
      ? Boolean(sessionStorage.getItem("webcheckin_token"))
      : false;

  const isInitialLoading =
    isReservationLoading ||
    isAuthLoading ||
    (Boolean(storedStructureId) && !structure) ||
    (hasReservationToken && !reservation);

  if (isInitialLoading && !structure && !reservation) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="flex flex-col items-center justify-center py-16 px-4 text-center gap-3"
      >
        <Spinner className="size-7 text-primary" />
        <p className="text-sm font-medium text-muted-foreground">
          {t(
            "welcomePage.loadingStay",
            "Caricamento dei dati della struttura in corso...",
          )}
        </p>
      </div>
    );
  }

  if (!structure && !reservation) {
    return (
      <div className="py-12 px-4 max-w-lg mx-auto">
        <Card className="border border-border bg-card shadow-none">
          <CardHeader className="space-y-1">
            <CardTitle className="text-xl font-bold">
              {t("welcomePage.emptyTitle", "Nessuna struttura selezionata")}
            </CardTitle>
            <CardDescription className="text-sm leading-relaxed">
              {t(
                "welcomePage.emptyDescription",
                "Accedi tramite il link ricevuto via email dall'hotel oppure inserisci il codice della tua prenotazione.",
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              variant="outline"
              className="min-h-11 gap-2 cursor-pointer"
              onClick={() => {
                if (storedStructureId) {
                  getStructure(storedStructureId);
                }
                if (hasReservationToken) {
                  refreshReservation();
                } else {
                  window.location.reload();
                }
              }}
            >
              <RefreshCw className="size-4" aria-hidden="true" />
              <span>{t("welcomePage.retryAction", "Ricarica pagina")}</span>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const reservationData = reservation as WebCheckInReservationData | null;
  const activeRes = reservationData?.reservation;
  const hasActiveReservation = Boolean(activeRes?.id);

  const rooms = Array.isArray(activeRes?.rooms) ? activeRes.rooms : [];
  const totalExpectedGuests =
    reservationData?.totalExpectedGuests ??
    rooms.reduce((acc, r) => acc + (r.guestsNumber || 0), 0);
  const totalCheckedInGuests =
    reservationData?.totalCheckedInGuests ??
    (Array.isArray(reservationData?.alreadyCheckedInGuests)
      ? reservationData.alreadyCheckedInGuests.length
      : rooms.reduce((acc, r) => acc + (r.guestIndex || 0), 0));

  const isCheckInCompleted = Boolean(
    reservationData &&
      (reservationData.isFullyCompleted ||
        (rooms.length > 0 &&
          rooms.every(
            (room) => (room.guestIndex || 0) >= (room.guestsNumber || 1),
          ))),
  );

  const guestFirstName = (
    profile?.name ||
    reservationData?.guest?.name ||
    activeRes?.name ||
    ""
  ).trim();

  const structureName =
    structure?.name || reservation?.structure?.name || "";

  const headingText = guestFirstName
    ? t("welcomePage.greetingWithName", {
        name: guestFirstName,
        defaultValue: `Benvenuto, ${guestFirstName}`,
      })
    : structureName
      ? t("welcomePage.greetingDefault", {
          structure: structureName,
          defaultValue: `Benvenuto in ${structureName}`,
        })
      : t("welcomePage.greetingFallback", "Il tuo soggiorno digitale");

  const addressLine = [structure?.address, structure?.city]
    .filter(Boolean)
    .join(", ");

  const mapsHref =
    structure?.googleMapsPosition ||
    (addressLine
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
          addressLine,
        )}`
      : null);

  const servicesList = Array.isArray(structure?.services)
    ? structure.services.filter(
        (service: any) =>
          service?.isActive !== false &&
          !service?.serviceId &&
          !service?.subServiceCategoryId,
      )
    : [];
  const allServicesInformative =
    servicesList.length > 0 &&
    servicesList.every((service: any) => service.serviceType === 2);
  const highlightedServices = servicesList.filter((service: any) =>
    Boolean(service?.highlighted ?? service?.Highlighted),
  );
  const previewServices = (
    highlightedServices.length > 0 ? highlightedServices : servicesList
  ).slice(0, 4);

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return null;
    const parsed = new Date(dateStr);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed.toLocaleDateString(
      i18n.language?.startsWith("en") ? "en-GB" : "it-IT",
      {
        day: "2-digit",
        month: "short",
        year: "numeric",
      },
    );
  };

  const arrivalFormatted = formatDate(activeRes?.arrival);
  const departureFormatted = formatDate(activeRes?.departure);
  const bookingReference = activeRes?.idReservationPms || activeRes?.id;

  return (
    <div className="w-full space-y-6 sm:space-y-8 pb-6">
      {/* Section 1: Orientation Header & Active Stay Focal Hub */}
      <section aria-labelledby="welcome-heading" className="space-y-5">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="space-y-2 max-w-2xl">
            {structureName && guestFirstName && (
              <p className="text-xs sm:text-sm font-semibold text-primary">
                {structureName}
              </p>
            )}
            <h1
              id="welcome-heading"
              className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground leading-tight"
            >
              {headingText}
            </h1>
            <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
              {hasActiveReservation
                ? t(
                    "welcomePage.subtitleWithReservation",
                    "Gestisci il check-in, richiedi servizi in struttura e consulta le informazioni utili per il tuo soggiorno.",
                  )
                : t(
                    "welcomePage.subtitleWithoutReservation",
                    "Consulta i servizi disponibili in struttura, le indicazioni stradali e i luoghi consigliati nei dintorni.",
                  )}
            </p>
            {addressLine && (
              <p className="flex items-center gap-1.5 text-xs sm:text-sm text-muted-foreground pt-0.5">
                <MapPin
                  className="size-4 text-primary shrink-0"
                  aria-hidden="true"
                />
                <span>{addressLine}</span>
              </p>
            )}
          </div>

          {(structure?.tel || mapsHref) && (
            <div className="flex flex-wrap items-center gap-2 shrink-0">
              {structure?.tel && (
                <Button
                  asChild
                  variant="outline"
                  className="min-h-11 px-3.5 gap-2 shadow-none"
                >
                  <a href={`tel:${structure.tel}`}>
                    <Phone
                      className="size-4 text-primary"
                      aria-hidden="true"
                    />
                    <span>
                      {t("welcomePage.callReception", "Chiama reception")}
                    </span>
                  </a>
                </Button>
              )}
              {mapsHref && (
                <Button
                  asChild
                  variant="outline"
                  className="min-h-11 px-3.5 gap-2 shadow-none"
                >
                  <a
                    href={mapsHref}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Navigation
                      className="size-4 text-primary"
                      aria-hidden="true"
                    />
                    <span>
                      {t("welcomePage.directions", "Indicazioni stradali")}
                    </span>
                  </a>
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Active Reservation & Web Check-In Focal Block */}
        {hasActiveReservation && (
          <div className="rounded-lg border border-border bg-muted/30 p-4 sm:p-5 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border/80 pb-3.5">
              <div className="flex items-center gap-2">
                <ClipboardCheck
                  className="size-5 text-primary shrink-0"
                  aria-hidden="true"
                />
                <h2 className="text-base sm:text-lg font-semibold text-foreground">
                  {t("welcomePage.staySectionTitle", "La tua prenotazione")}
                </h2>
              </div>

              {bookingReference && (
                <Badge variant="outline" className="font-mono text-xs">
                  {t("webCheckIn.bookingCode", "Prenotazione")} #
                  {bookingReference}
                </Badge>
              )}
            </div>

            {/* Stay Metadata Grid: 1 col mobile -> 3 cols tablet/desktop */}
            {(arrivalFormatted ||
              departureFormatted ||
              rooms.length > 0) && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                {arrivalFormatted && (
                  <div className="flex items-center gap-2.5 rounded-md bg-background p-3 border border-border/60">
                    <Calendar
                      className="size-4 text-primary shrink-0"
                      aria-hidden="true"
                    />
                    <div>
                      <p className="text-xs text-muted-foreground">
                        {t("webCheckIn.arrival", "Arrivo")}
                      </p>
                      <p className="font-semibold text-foreground">
                        {arrivalFormatted}
                      </p>
                    </div>
                  </div>
                )}

                {departureFormatted && (
                  <div className="flex items-center gap-2.5 rounded-md bg-background p-3 border border-border/60">
                    <Calendar
                      className="size-4 text-primary shrink-0"
                      aria-hidden="true"
                    />
                    <div>
                      <p className="text-xs text-muted-foreground">
                        {t("webCheckIn.departure", "Partenza")}
                      </p>
                      <p className="font-semibold text-foreground">
                        {departureFormatted}
                      </p>
                    </div>
                  </div>
                )}

                {rooms.length > 0 && (
                  <div className="flex items-center gap-2.5 rounded-md bg-background p-3 border border-border/60">
                    <BedDouble
                      className="size-4 text-primary shrink-0"
                      aria-hidden="true"
                    />
                    <div>
                      <p className="text-xs text-muted-foreground">
                        {t("webCheckIn.roomsCount", {
                          count: rooms.length,
                          defaultValue:
                            rooms.length === 1
                              ? "1 camera"
                              : `${rooms.length} camere`,
                        })}
                      </p>
                      <p className="font-semibold text-foreground truncate">
                        {totalExpectedGuests > 0
                          ? t("webCheckIn.guestsProgress", {
                              checked: totalCheckedInGuests,
                              total: totalExpectedGuests,
                              defaultValue: `${totalCheckedInGuests} di ${totalExpectedGuests} ospiti registrati`,
                            })
                          : rooms[0]?.description ||
                            rooms[0]?.roomNumber ||
                            "-"}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Check-in Status & Primary CTA */}
            {isCheckInCompleted ? (
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-1">
                <div className="flex items-start gap-2.5">
                  <CheckCircle2
                    className="size-5 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5"
                    aria-hidden="true"
                  />
                  <div className="space-y-0.5">
                    <p className="text-sm font-semibold text-foreground">
                      {t(
                        "webCheckIn.completedText",
                        "Check-in online completato",
                      )}
                    </p>
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      {t(
                        "webCheckIn.completedDescription",
                        "I dati degli ospiti sono stati registrati. Puoi consultare il riepilogo della prenotazione in qualsiasi momento.",
                      )}
                    </p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 shrink-0">
                  <Button
                    asChild
                    variant="outline"
                    className="min-h-11 px-4 shadow-none"
                  >
                    <Link to="/check-in">
                      {t("verification.editData", "Modifica dati")}
                    </Link>
                  </Button>
                  <Button asChild className="min-h-11 px-4">
                    <Link to="/completed">
                      {t(
                        "webCheckIn.viewSummaryAction",
                        "Vedi riepilogo check-in",
                      )}
                    </Link>
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-1">
                <div className="space-y-0.5 max-w-xl">
                  <p className="text-sm font-semibold text-foreground">
                    {t("webCheckIn.title", "Web Check-In")}
                  </p>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    {t(
                      "webCheckIn.description",
                      "Registra i documenti degli ospiti prima dell'arrivo per ritirare subito le chiavi in reception.",
                    )}
                  </p>
                </div>

                <Button
                  asChild
                  size="lg"
                  className="min-h-11 px-6 font-semibold shrink-0 w-full sm:w-auto"
                >
                  <Link to="/check-in">
                    {totalCheckedInGuests > 0
                      ? t(
                          "webCheckIn.continueAction",
                          "Continua il Web Check-In",
                        )
                      : t("webCheckIn.action", "Inizia il Web Check-In")}
                  </Link>
                </Button>
              </div>
            )}
          </div>
        )}
      </section>

      {/* Section 2: Guest Area & Fidelity */}
      <GuestAreaPromoBanner />

      {/* Section 3: Concierge Services & Local Territory (Asymmetric 7/5 Desktop Reflow) */}
      <section
        aria-labelledby="concierge-heading"
        className="space-y-3 sm:space-y-4"
      >
        <h2
          id="concierge-heading"
          className="text-lg sm:text-xl font-bold tracking-tight text-foreground"
        >
          {t("welcomePage.conciergeSectionTitle", "Servizi e soggiorno")}
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-4 sm:gap-6 items-stretch">
          {/* Primary Destination: Hotel Services (7 cols on lg) */}
          <Card className="md:col-span-1 lg:col-span-7 border border-border bg-card shadow-none flex flex-col justify-between">
            <CardHeader className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <div
                  className="size-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0"
                  aria-hidden="true"
                >
                  <HandPlatter className="size-5" />
                </div>
                {servicesList.length > 0 && (
                  <Badge variant="secondary" className="text-xs font-medium">
                    {t("welcomePage.availableServicesCount", {
                      count: servicesList.length,
                      defaultValue:
                        servicesList.length === 1
                          ? "1 servizio disponibile"
                          : `${servicesList.length} servizi disponibili`,
                    })}
                  </Badge>
                )}
              </div>
              <CardTitle className="text-xl font-bold text-foreground">
                {t("services.title", "Servizi")}
              </CardTitle>
              <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                {t("services.description", "Visualizza i servizi offerti")}
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-4 pt-0">
              {previewServices.length > 0 && (
                <ul
                  aria-label={t("services.title", "Servizi")}
                  className="divide-y divide-border rounded-lg border border-border/80 bg-muted/20"
                >
                  {previewServices.map((srv: any) => {
                    const srvName = getTranslatedName(
                      srv.name || srv.title,
                      i18n.language,
                    );
                    if (!srvName) return null;
                    return (
                      <li key={srv.id}>
                        <Link
                          to={`/services/${srv.id}`}
                          className="flex items-center justify-between gap-3 px-3.5 py-2.5 min-h-11 text-sm font-medium text-foreground hover:bg-accent/60 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          <span className="truncate">{srvName}</span>
                          <ChevronRight
                            className="size-4 text-muted-foreground shrink-0"
                            aria-hidden="true"
                          />
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              )}

              <Button asChild className="w-full min-h-11 font-semibold">
                <Link to="/services">
                  {allServicesInformative
                    ? t("services.discoverAction", "Scopri i servizi")
                    : t("services.action", "Richiedi i servizi")}
                </Link>
              </Button>
            </CardContent>
          </Card>

          {/* Supporting Destinations: Points of Interest & Hotel Info (5 cols on lg) */}
          <div className="md:col-span-1 lg:col-span-5 flex flex-col gap-4 sm:gap-6 justify-between">
            <Card className="border border-border bg-card shadow-none flex-1 flex flex-col justify-between">
              <CardHeader className="space-y-1.5 pb-3">
                <div className="flex items-center gap-3">
                  <div
                    className="size-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0"
                    aria-hidden="true"
                  >
                    <MapPinned className="size-5" />
                  </div>
                  <div>
                    <CardTitle className="text-lg font-bold text-foreground">
                      {t("pointOfInterest.title", "Punti di interesse")}
                    </CardTitle>
                    <CardDescription className="text-xs sm:text-sm text-muted-foreground">
                      {t(
                        "pointOfInterest.description",
                        "Scopri i punti di interesse vicino a te",
                      )}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <Button
                  asChild
                  variant="outline"
                  className="w-full min-h-11 font-medium shadow-none"
                >
                  <Link to="/point-of-interest">
                    {t("pointOfInterest.listAction", "Visualizza luoghi")}
                  </Link>
                </Button>
              </CardContent>
            </Card>

            <Card className="border border-border bg-card shadow-none flex-1 flex flex-col justify-between">
              <CardHeader className="space-y-1.5 pb-3">
                <div className="flex items-center gap-3">
                  <div
                    className="size-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0"
                    aria-hidden="true"
                  >
                    <Building2 className="size-5" />
                  </div>
                  <div>
                    <CardTitle className="text-lg font-bold text-foreground">
                      {t("hotelInfo.title", "Informazioni sull'hotel")}
                    </CardTitle>
                    <CardDescription className="text-xs sm:text-sm text-muted-foreground">
                      {t(
                        "welcomePage.hotelInfoDescription",
                        "Orari, contatti diretti della reception e indicazioni per raggiungere la struttura.",
                      )}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <Button
                  asChild
                  variant="outline"
                  className="w-full min-h-11 font-medium shadow-none"
                >
                  <Link to="/hotel-info">
                    {t(
                      "welcomePage.hotelInfoAction",
                      "Apri informazioni struttura",
                    )}
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
}
